'use client';

import styles from './SectorWhyUs.module.scss';
import MenuLink from '@/src/components/MenuLink/MenuLink';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

export interface TrustLogo {
  img: string;
  alt: string;
}

export interface SectorWhyUsProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  clientsLabel?: string;
  clients?: (TrustLogo | string)[];
}

function isLogoImage(client: TrustLogo | string): client is TrustLogo {
  return typeof client === 'object' && client !== null && 'img' in client;
}

export function SectorWhyUs({
  eyebrow = 'Pourquoi nous',
  title = 'Notre expérience sur le secteur',
  description = '',
  ctaLabel,
  ctaHref,
  clientsLabel = "Une expertise forgée au sein d'organisations exigeantes",
  clients = [],
}: SectorWhyUsProps) {
  useAOS();

  if (!description && clients.length === 0) {
    return null;
  }

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.content} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          {title && <h2 className={styles.title}>{title}</h2>}
          {description && <p className={styles.description}>{description}</p>}
          {ctaLabel && ctaHref && (
            <MenuLink
              href={ctaHref}
              className={styles.cta}
              onClick={() => gaCta(ctaLabel, 'sector_why_us_cta', ctaHref)}
            >
              {ctaLabel}
            </MenuLink>
          )}
        </div>
        {clients.length > 0 && (
          <div
            className={styles.clients}
            data-aos="fade-up"
            data-aos-duration="900"
            data-aos-delay="150"
          >
            {clientsLabel && <p className={styles.clientsLabel}>{clientsLabel}</p>}
            <div className={styles.logos}>
              {clients.map((client, index) =>
                isLogoImage(client) ? (
                  <div key={`${client.img}-${index}`} className={styles.logoWrapper}>
                    <img
                      src={client.img}
                      alt={client.alt}
                      className={styles.logoImage}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <span key={`${client}-${index}`} className={styles.logoText}>
                    {client}
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
